import { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import { gltfWithAuth } from "./gltfAuth";
import * as THREE from "three";

// Renders a built-but-not-yet-committed SLAM map (the staged preview GLB) so the operator can
// inspect it before choosing keep/discard (decisions.md#77). Auto-fits and frames the model;
// drag to orbit. Independent of the warehouse model — this is the staged object, not /live.
function PreviewGLB({ url }: { url: string }) {
  const { scene } = useGLTF(url, true, true, gltfWithAuth);
  const clone = useMemo(() => {
    const c = scene.clone(true);
    // Centre the model on the origin and lay it flat-ish so the auto-framed camera sees it.
    const box = new THREE.Box3().setFromObject(c);
    const center = new THREE.Vector3();
    box.getCenter(center);
    c.position.sub(center);
    return c;
  }, [scene]);
  return <primitive object={clone} />;
}

export function MapPreviewView({ modelUrl }: { modelUrl: string }) {
  // Gateway-relative URL (/v1/models/...) — fetched as-is, same as WarehouseModel.
  const url = modelUrl;
  const prevUrl = useRef<string | null>(null);
  useEffect(() => {
    if (prevUrl.current && prevUrl.current !== url) useGLTF.clear(prevUrl.current);
    useGLTF.preload(url, true, true, gltfWithAuth);
    prevUrl.current = url;
    return () => { useGLTF.clear(url); };
  }, [url]);

  return (
    <div style={{ width: "100%", height: 300, borderRadius: 12, overflow: "hidden", background: "#0A1B3D" }}>
      <Canvas camera={{ position: [14, 16, 18], fov: 45 }}>
        <ambientLight intensity={0.8} />
        <directionalLight position={[10, 20, 10]} intensity={1.1} castShadow />
        {/* No grid plane: it z-fights the model's baked floor (flicker) — the map floats. */}
        <Suspense fallback={null}>
          <PreviewGLB key={url} url={url} />
        </Suspense>
        <OrbitControls makeDefault enablePan target={[0, 0, 0]} />
      </Canvas>
    </div>
  );
}
